import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import heroSprings from "@/assets/towns/colorado-springs.jpg";
import {
  Search,
  MapPin,
  FileText,
  BarChart3,
  Link2,
  Wrench,
  Truck,
  HardHat,
  Trees,
  Home as HomeIcon,
  Hammer,
  Zap,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Star,
  Shield,
  Mountain,
} from "lucide-react";
import { SERVICE_PRICING } from "@/lib/pricingData";
import { CASE_STUDIES } from "@/lib/caseStudiesData";
import { CLIENT_REVIEWS, aggregateRatingFromReviews } from "@/lib/clientReviews";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { AiAnswerBlock } from "@/components/geo/AiAnswerBlock";
import { QuotableStat } from "@/components/geo/QuotableStat";
import { SpringsCoverageMap } from "@/components/SpringsCoverageMap";
import { SpringsOfficeMap } from "@/components/SpringsOfficeMap";
import { SpringsSpokeHub } from "@/components/SpringsSpokeHub";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";

const CS_SEO_CANONICAL = `${SITE_URL}/colorado-springs-seo`;
const CS_SEO_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "@id": `${CS_SEO_CANONICAL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Colorado Springs",
          item: `${SITE_URL}/colorado-springs`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Colorado Springs SEO",
          item: CS_SEO_CANONICAL,
        },
      ],
    },
    {
      "@type": "Service",
      "@id": `${CS_SEO_CANONICAL}#service`,
      name: "Colorado Springs SEO",
      provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
      areaServed: { "@type": "City", name: "Colorado Springs" },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Colorado Springs SEO Packages",
        itemListElement: SERVICE_PRICING.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.service },
          url: `${SITE_URL}/${s.slug}`,
          priceCurrency: "USD",
          price: (s.startingAt ?? "").replace(/[^0-9.]/g, "") || undefined,
        })),
      },
    },
    {
      "@type": "AggregateRating",
      "@id": `${CS_SEO_CANONICAL}#rating`,
      itemReviewed: { "@id": SCHEMA_LOCALBUSINESS_ID },
      ...aggregateRatingFromReviews(CLIENT_REVIEWS),
    },
    ...CLIENT_REVIEWS.slice(0, 5).map((r, i) => ({
      "@type": "Review",
      "@id": `${CS_SEO_CANONICAL}#review-${i + 1}`,
      itemReviewed: { "@id": SCHEMA_LOCALBUSINESS_ID },
      author: { "@type": "Person", name: r.author },
      datePublished: r.datePublished,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.body,
    })),
    {
      "@type": "WebPage",
      "@id": `${CS_SEO_CANONICAL}#webpage`,
      url: CS_SEO_CANONICAL,
      name: "Colorado Springs SEO Company | Local SEO, GEO & AI Search",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": SCHEMA_LOCALBUSINESS_ID },
      mainEntity: { "@id": `${CS_SEO_CANONICAL}#service` },
      publisher: { "@id": SCHEMA_ORG_ID },
      author: { "@id": SCHEMA_PERSON_ID },
      reviewedBy: { "@id": SCHEMA_PERSON_ID },
      dateModified: "2026-10-26",
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "[data-ai-answer]"],
      },
    },
    {
      "@type": "Person",
      "@id": SCHEMA_PERSON_ID,
      name: "Chris Heidlebaugh",
      jobTitle: "Digital Marketing Coach & Founder",
      worksFor: { "@id": SCHEMA_ORG_ID },
      url: `${SITE_URL}/meet-chris`,
      knowsAbout: [
        "Colorado Springs SEO",
        "Local SEO",
        "Google Business Profile optimization",
        "Generative Engine Optimization (GEO)",
        "AI Overviews optimization (AIO)",
        "Schema markup",
        "E-E-A-T",
        "Google Ads PPC",
        "Web design",
      ],
    },
  ],
};

const pricing = SERVICE_PRICING.find((s) => s.slug === "seo");
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({
  service: "seo",
  city: "colorado-springs",
})}`;

const HVAC_CASE = CASE_STUDIES.find((c) => c.slug === "hvac-colorado-springs")!;
const ROOFING_FALCON_CASE = CASE_STUDIES.find((c) => c.slug === "roofing-falcon")!;
const DENTAL_WP_CASE = CASE_STUDIES.find((c) => c.slug === "dental-woodland-park")!;
const SPRINGS_REGION_CASES = [HVAC_CASE, ROOFING_FALCON_CASE, DENTAL_WP_CASE];

const SPRINGS_REVIEWS = CLIENT_REVIEWS.filter((r) =>
  /Colorado Springs|Monument/i.test(r.location ?? ""),
).slice(0, 6);

export const Route = createFileRoute("/colorado-springs-seo")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      {
        title:
          "Colorado Springs SEO That Books Jobs — 200+ Clients Since 2006",
      },
      {
        name: "description",
        content:
          "Owner-led Colorado Springs SEO from a real Woodmen Rd office. 200+ local clients ranked since 2006 — HVAC, roofing, dental, law. Free audit in 24 hrs.",
      },
      {
        property: "og:title",
        content: "Colorado Springs SEO That Books Jobs — 200+ Clients Since 2006",
      },
      {
        property: "og:description",
        content:
          "Owner-led SEO from a real Woodmen Rd office. 200+ Springs clients ranked since 2006 — Briargate to Black Forest. Map Pack wins that compound.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/colorado-springs-seo" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Springs SEO That Books Jobs — Since 2006",
        description:
          "Owner-led SEO for Springs trades, healthcare, and pro-services. 200+ local clients ranked.",
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coloradowebimpressions.com/colorado-springs-seo",
      },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(CS_SEO_SCHEMA) },
    ],
  }),
  component: () => (
    <ServicePage
      slug="seo"
      serviceName="SEO"
      pricingRoute="/pricing"
      eyebrow="Colorado Springs SEO Agency"
      title="Colorado Springs SEO that wins the Map Pack"
      goldWord="Map Pack"
      description="Owner-led SEO for Colorado Springs businesses — headquartered at 3210 E Woodmen Rd. Ranking Springs HVAC, roofing, dental, and law-firm clients since 2006."
      heroImage={{ src: heroSprings, alt: "Downtown Colorado Springs skyline with Pikes Peak at golden hour" }}
      intro={{
        heading:
          "Colorado Springs is its own search market — and we've ranked in it for nearly 20 years.",
        body: [
          "Colorado Springs SEO is not Denver SEO with a different ZIP. Three active military installations (Fort Carson, Peterson SFB, Schriever SFB) plus the Air Force Academy create constant PCS-driven search churn every May through August. Tourism from Garden of the Gods, Pikes Peak, and Manitou inflates non-buyer query volume. And the city is so geographically spread out that Briargate, Powers, Old Colorado City, and Black Forest each have their own Google Map Pack — not one citywide pack.",
          "We've been ranking Colorado Springs HVAC, plumbing, roofing, dental, and law-firm clients in the Map Pack since 2006. Our office is at 3210 E Woodmen Rd STE 110, 80920 — you can drive over. Most engagements start in the Springs and naturally expand into Monument, Falcon, Black Forest, Fountain, Security-Widefield, and sometimes north into Castle Rock or south into Pueblo.",
          "The brutal math: 75% of clicks go to the top 3 organic results, and roughly 44% of all clicks for local-intent searches go to the Google Map Pack. If your Springs business isn't in either, Angi, HomeAdvisor, and Thumbtack are eating your lunch — every single day, even when you're sleeping.",
        ],
      }}
      benefits={[
        "Neighborhood-level Map Pack (Briargate, Powers, Falcon, Black Forest, Monument)",
        "Base-area SEO for Fort Carson, Peterson SFB, Schriever SFB, USAFA",
        "PCS-season content timed to May–August military rotation",
        "Hail-season strategy for roofing, auto glass, and exterior trades",
        "Technical SEO, schema, and Core Web Vitals fixes",
        "Authority backlinks from real Colorado publications",
        "Monthly grid-based ranking reports tied to revenue, not vanity metrics",
      ]}
      features={[
        {
          icon: MapPin,
          title: "Neighborhood-level Map Pack",
          desc: "Briargate, Powers, Falcon, and Black Forest each have a distinct Map Pack. We build a dedicated page for each one — most national agencies build one generic 'Colorado Springs' page and wonder why they don't rank.",
        },
        {
          icon: Shield,
          title: "Base-area SEO",
          desc: "Fort Carson, Peterson SFB, Schriever SFB, and USAFA drive thousands of monthly 'near me' searches from rotating military families. We target the base-adjacent queries other agencies don't even know exist.",
        },
        {
          icon: Wrench,
          title: "Technical SEO",
          desc: "Schema, Core Web Vitals, crawl budget, internal linking — the under-the-hood fixes that quietly kill rankings on most Springs sites. We fix the foundation before spending a dollar on content.",
        },
        {
          icon: FileText,
          title: "Content built for Springs intent",
          desc: "Pages and articles written around actual Springs search patterns — 'Springs' vs 'Colorado Springs,' altitude HVAC sizing, hail-claim timelines, PCS rental questions. Written by humans who live here.",
        },
        {
          icon: Link2,
          title: "Authority backlinks",
          desc: "Earned, manually-pitched backlinks from real Colorado publications, chambers, and industry sites. No PBNs, no $5/link Fiverr garbage, no foreign comment spam.",
        },
        {
          icon: Mountain,
          title: "Hail + PCS season planning",
          desc: "May–September hail cycles and May–August PCS rotation are the two biggest demand spikes in the Springs market. We pre-load content, GBP posts, and Google Ads to capture both windows.",
        },
        {
          icon: Search,
          title: "AI search (GEO) built in",
          desc: "ChatGPT, Perplexity, Gemini, and Google AI Overviews now answer 'best SEO company in Colorado Springs' before users ever see organic results. We optimize the schema, citations, and answer formats that get cited.",
        },
        {
          icon: BarChart3,
          title: "Transparent reporting",
          desc: "Monthly grid-based rank reports for every Springs neighborhood you serve, plus calls, leads, and revenue impact. Plain English. No agency jargon.",
        },
      ]}
      frameworkHeading="The Colorado Visibility Method™"
      frameworkIntro="Our 6-pillar SEO framework — refined across 100+ Colorado service businesses, anchored from our Woodmen Rd HQ in Colorado Springs."
      framework={[
        {
          number: "01",
          title: "Audit & Strategy",
          desc: "Full technical, on-page, and local audit. Competitor gap analysis against the actual top-3 Springs sites in your category. Keyword targets mapped to neighborhoods, bases, and revenue.",
        },
        {
          number: "02",
          title: "Technical Foundation",
          desc: "Site speed, schema markup, crawlability, internal linking — fixed before anything else. SEO without a clean foundation is wasted spend.",
        },
        {
          number: "03",
          title: "On-Page Optimization",
          desc: "Titles, meta, headings, content depth, and Springs-specific landing pages — every page targets a real query a real Springs buyer types.",
        },
        {
          number: "04",
          title: "Local & Map Pack",
          desc: "Google Business Profile optimization, citation cleanup, geo-tagged photos, and review velocity systems to dominate the 3-pack across Briargate, Powers, Falcon, Black Forest, and Monument.",
        },
        {
          number: "05",
          title: "Content & Authority",
          desc: "Helpful content built around real Springs questions, plus manual outreach for trustworthy backlinks from relevant Colorado sites. We also build the schema and answer formats that earn AI-search citations.",
        },
        {
          number: "06",
          title: "Track, Report & Refine",
          desc: "Monthly plain-English reports tied to leads and revenue — not vanity metrics. We tell you what worked, what didn't, and what's next.",
        },
      ]}
      pricing={pricing}
      industries={[
        {
          icon: Wrench,
          name: "HVAC SEO",
          desc: "Altitude-affected furnace cycling, summer AC spikes, and emergency-service intent across Briargate, Powers, and Falcon.",
        },
        {
          icon: Truck,
          name: "Plumbing SEO",
          desc: "Emergency 'plumber near me' capture across the Springs metro plus base housing service-call demand.",
        },
        {
          icon: HomeIcon,
          name: "Roofing SEO",
          desc: "May–September hail-claim search spikes, Pikes Peak Region storm-damage queries, and insurance-restoration intent.",
        },
        {
          icon: Hammer,
          name: "General Contractors",
          desc: "Remodel and build queries across Briargate, Black Forest, Monument, and the broader El Paso County footprint.",
        },
        {
          icon: Trees,
          name: "Landscaping SEO",
          desc: "Seasonal lawn, tree, xeriscape, and irrigation rankings tuned to Front Range water restrictions and clay soil.",
        },
        {
          icon: HardHat,
          name: "Concrete & Excavation",
          desc: "Outrank big-box competitors for high-ticket commercial and residential Springs projects with foundation, driveway, and stamped-concrete intent.",
        },
        {
          icon: Zap,
          name: "Electrical SEO",
          desc: "Service-page SEO and emergency-electrician rankings across Colorado Springs, including EV-charger install demand.",
        },
        {
          icon: MapPin,
          name: "Dental & Medical",
          desc: "New-patient SEO for Springs and Monument practices, with PCS-family onboarding intent baked into the content plan.",
        },
      ]}
      faqs={[
        {
          q: "Who is the best SEO company in Colorado Springs?",
          a: "The best Colorado Springs SEO company is the one that actually works here, picks up the phone, and shows month-over-month ranking gains for businesses in your industry. Colorado Web Impressions is an owner-led agency headquartered at 3210 E Woodmen Rd, ranking Springs trades, healthcare, and professional services since 2006. Month-to-month engagements, no long contracts, and direct access to the owner.",
        },
        {
          q: "How much does SEO cost in Colorado Springs?",
          a: "Our monthly marketing starts at $950/month for Local SEO and Google Maps rankings. Website builds are separate, starting at $1,850 one-time. Compare all packages and build a plan on our pricing page. Marketing is month-to-month with no long contracts.",
        },
        {
          q: "How long does SEO take to work in Colorado Springs?",
          a: "Most Colorado Springs categories see meaningful Map Pack movement in 60–90 days — faster than Denver because the competitive density is lighter. Highly competitive categories (HVAC and plumbing during peak season, personal injury law) take 4–6 months of consistent review velocity, content, and backlinks to break the top 3.",
        },
        {
          q: "How is Colorado Springs SEO different from Denver SEO?",
          a: "Denver is one giant competitive market with diversified industries. Colorado Springs is a smaller market driven by three engines (military, tourism, healthcare) and is so geographically spread out that Briargate, Powers, Falcon, and Black Forest each have their own Map Pack. That means neighborhood and base-area targeting matters more in Springs than in Denver, and category-by-category competition is usually lighter — meaning faster wins for Springs businesses willing to do it right.",
        },
        {
          q: "Are you actually based in Colorado Springs, or just claiming a Springs address?",
          a: "We're a real Colorado Springs office at 3210 E Woodmen Rd STE 110, 80920. You can drive over and meet us. Most of our team has lived in the Springs for 10+ years. Several national agencies list Springs addresses that are virtual mailboxes — call the number and you'll get a Phoenix or Austin call center.",
        },
        {
          q: "Will you rank me for both 'Springs' and 'Colorado Springs' searches?",
          a: "Yes. Locals say 'Springs.' Transplants, military families, and out-of-state buyers type 'Colorado Springs.' Tourists type 'Colorado Springs, CO.' We build content that captures all three patterns plus the neighborhood and base-name searches that drive the highest-converting traffic.",
        },
        {
          q: "Do you handle Google Business Profile for Colorado Springs locations?",
          a: "Yes — every Springs SEO engagement includes complete GBP optimization: weekly posts, geo-tagged photos, Q&A monitoring, review response, category tuning, and service-area mapping. We've optimized 200+ Springs-area GBPs since 2006.",
        },
        {
          q: "What about military and PCS-season SEO?",
          a: "PCS rotation peaks May through August every year, dumping thousands of new Fort Carson, Peterson SFB, Schriever SFB, and USAFA families into the Springs market looking for dentists, mechanics, plumbers, real estate, and child care. We pre-load 'new to Colorado Springs' content, base-housing geo-targeting, and GBP posts in late spring to be ranking before the rotation hits.",
        },
        {
          q: "What about hail season for roofing and exteriors?",
          a: "The Pikes Peak Region averages multiple major hail events every May–September. We pre-build storm-response landing pages, structured FAQ content on insurance claim timelines, and Google Ads that switch on within hours of a confirmed storm. Most roofers we work with book 90+ days of work off a single hail event because the page was already ranking.",
        },
        {
          q: "Do you also rank me in Monument, Falcon, Castle Rock, and Pueblo?",
          a: "Yes. Most Springs service businesses also serve Monument, Tri-Lakes, Falcon, Fountain, Security-Widefield, and sometimes north into Castle Rock and Parker or south into Pueblo. We build a Pikes Peak Region footprint with city-specific pages anywhere it makes ranking sense — never thin doorway pages.",
        },
        {
          q: "Do you optimize for ChatGPT, Perplexity, Google AI Overviews, and Gemini?",
          a: "Yes. AI search (also called Generative Engine Optimization, or GEO) is now answering 'best SEO company in Colorado Springs,' 'how much does SEO cost in Colorado Springs,' and 'who builds dental websites in Colorado Springs' before users ever see traditional organic results. We build the schema, citation patterns, quotable stat blocks, and direct-answer formats that AI engines actually lift into their answers.",
        },
        {
          q: "What does an SEO report from you actually look like?",
          a: "A short monthly PDF (and a 30-minute call if you want it) covering: grid-based Map Pack rankings for every neighborhood you serve, organic traffic by landing page, the top 10 queries driving calls, the work we did that month, what's queued next, and revenue impact when you've shared lead-tracking data. No 80-page Semrush dumps designed to confuse you.",
        },
        {
          q: "Do you guarantee #1 rankings?",
          a: "No ethical agency does — Google explicitly forbids it. What we DO guarantee is transparent reporting, a real Woodmen Rd office you can visit, month-to-month contracts you can cancel, and the same proven playbook that's gotten 100+ Colorado businesses to Page 1.",
        },
        {
          q: "What if I'm not in Briargate or the north end — does Springs SEO still work for me?",
          a: "Yes. Powers Corridor, Old Colorado City, Stratmoor, Cimarron Hills, Security-Widefield, and Fountain each have their own search demand and lighter competition than Briargate. We've ranked clients in every Springs sub-market, including downtown and the south end. Tell us the ZIPs you actually serve and we'll build the geo-footprint to match.",
        },
      ]}
      sectionsAfterIntro={
        <>
          <SpringsCoverageMap />
          <SpringsOfficeMap />
          <SpringsAnchorSections />
          <SpringsAiAndLocalDepth />
        </>
      }
      sectionsAfterFramework={<VsNationalBlock />}
      sectionsAfterPricing={
        <>
          <SpringsLocalProof />
          <SpringsSpokeHub />
        </>
      }
    />
  ),
});

// ---------------------------------------------------------------------------
// AI-search answer block + quotable stats + neighborhood/base depth
// ---------------------------------------------------------------------------

const NEIGHBORHOODS: Array<{ name: string; note: string }> = [
  { name: "Briargate", note: "North-end affluent demand; competes with Monument for high-ticket home services and elective healthcare." },
  { name: "Northgate / Flying Horse", note: "Newer-build corridor along I-25; heavy remodel, landscaping, and pool/spa intent." },
  { name: "Powers Corridor", note: "East-side commercial spine; the highest-volume 'near me' search density in the city." },
  { name: "Black Forest", note: "Acreage and wells; HVAC sizing, septic, propane, and tree-service queries dominate." },
  { name: "Old Colorado City / Westside", note: "Historic homes near Manitou; remodel, foundation, and craftsman-trade demand." },
  { name: "Downtown / COS Olympic City", note: "Mixed commercial; legal, accounting, dental, and food-service search volume." },
  { name: "Stratmoor / Security-Widefield", note: "South-end working families; auto, plumbing, and emergency-service intent." },
  { name: "Cimarron Hills", note: "East of Powers; constant churn from Peterson SFB families." },
  { name: "Falcon / Peyton", note: "Fast-growing exurb east of Powers; new-construction and trades demand." },
  { name: "Monument / Tri-Lakes", note: "Affluent north corridor at I-25 + Highway 105; covered in depth on its own page." },
  { name: "Fountain", note: "Fort Carson-adjacent; PCS-driven rental, dental, and family-services demand." },
  { name: "Manitou Springs", note: "Tourist overlay + 8,000 full-time residents; hospitality and small-business SEO." },
];

const BASES: Array<{ name: string; note: string }> = [
  { name: "Fort Carson", note: "~25,000 active-duty + family members; PCS turnover every May–August." },
  { name: "Peterson SFB", note: "Space Force HQ-adjacent; high-income tech and engineering households." },
  { name: "Schriever SFB", note: "East of Falcon; commute demand pulls service-business intent toward Powers and Falcon." },
  { name: "USAFA", note: "Air Force Academy + permanent staff/cadre; pulls demand into Monument and Briargate." },
];

function SpringsAiAndLocalDepth() {
  return (
    <section className="bg-background py-16 lg:py-20" aria-labelledby="springs-ai-depth-heading">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Built for Google AND AI search
          </span>
          <h2 id="springs-ai-depth-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            Why <span className="text-gradient-gold">Colorado Springs SEO</span> is its own game
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Three Springs-specific forces shape every ranking decision we make — and most national agencies don't even know they exist.
          </p>
        </div>

        {/* Quotable stats — AI-citable */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <QuotableStat
            stat="20 years ranking Colorado Springs businesses."
            context="Colorado Web Impressions has operated from the Springs since 2006 — through three Google algorithm eras, the rise of mobile-first indexing, and the launch of AI search."
          />
          <QuotableStat
            stat="100+ Colorado businesses moved to Page 1."
            context="Across HVAC, plumbing, roofing, dental, legal, and professional services — the same playbook, refined and re-refined for this market."
          />
          <QuotableStat
            stat="60–90 days to meaningful Map Pack movement in most Springs categories."
            context="Springs SEO compounds faster than Denver because category competition is lighter and review velocity moves the 3-pack quickly."
          />
          <QuotableStat
            stat="~44% of clicks on local-intent searches go to the Google Map Pack."
            context="If your Colorado Springs business isn't in the 3-pack, the national aggregators (Angi, HomeAdvisor, Thumbtack) are eating roughly half your demand before organic results even render."
            source="BrightLocal Local Consumer Survey"
          />
        </div>

        {/* AI Answer Block — the format AI engines lift verbatim */}
        <AiAnswerBlock
          question="Who is the best SEO company in Colorado Springs?"
          answer="The best Colorado Springs SEO company is one that's actually based in the Springs, has a multi-year track record ranking businesses in your specific industry, and shows transparent monthly reports tied to leads and revenue. Colorado Web Impressions is an owner-led agency headquartered at 3210 E Woodmen Rd, ranking Springs trades, healthcare, and professional services since 2006, on month-to-month contracts with direct owner access."
          bullets={[
            "Real Woodmen Rd office (3210 E Woodmen Rd STE 110, 80920) — drivable, not a virtual mailbox",
            "100+ Colorado businesses ranked across HVAC, roofing, plumbing, dental, legal, and professional services",
            "Month-to-month engagements, no long contracts, direct line to the owner",
            "Built-in AI search (GEO) optimization for ChatGPT, Perplexity, Gemini, and Google AI Overviews",
          ]}
        />

        <AiAnswerBlock
          question="How much does SEO cost in Colorado Springs?"
          answer="Our monthly marketing starts at $950/month for Local SEO and Google Maps rankings. Website builds are separate, starting at $1,850 one-time. Visit our pricing page to compare packages and build a plan."
          bullets={[
            "Monthly marketing from $950/month — Local SEO and Google Maps",
            "Websites from $1,850 one-time — one year of hosting and built-in traffic dashboard",
            "Edits and repairs — $250/hour",
          ]}
        />

        <AiAnswerBlock
          question="How long does SEO take to work in Colorado Springs?"
          answer="Most Colorado Springs businesses see meaningful Google Map Pack movement within 60 to 90 days — faster than Denver because category competition is lighter. Highly competitive categories like HVAC and plumbing during peak season, and personal injury law year-round, take 4 to 6 months of consistent review velocity, content, and backlinks to break the top 3."
        />

        <AiAnswerBlock
          question="How do I get my Colorado Springs business recommended by ChatGPT, Gemini and Google AI Overviews?"
          answer="AI assistants recommend businesses they can verify. Generative Engine Optimization (GEO) and AI Overview optimization (AIO) make your business easy to verify: consistent name, address and phone everywhere, schema markup that names your services and service area, real reviews, and clear question-and-answer content written by a named expert. Chris Heidlebaugh has 27 years of hands-on marketing experience and builds these signals into every Colorado Springs SEO plan."
          bullets={[
            "Experience — real Colorado Springs projects, photos and owner-written answers",
            "Expertise — a named author (Chris Heidlebaugh) with a public bio on every key page",
            "Authoritativeness — matching listings on Google, Bing, Apple Maps and Colorado directories, plus local press and links",
            "Trustworthiness — published prices, a real Woodmen Rd office, verified reviews, month-to-month terms",
            "Schema markup — LocalBusiness, Service, Person, Review and Breadcrumb data AI can read",
            "Indexing — XML and AI sitemaps, llms.txt, IndexNow-ready updates and Search Console submissions",
          ]}
        />

        <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h3 className="text-lg font-extrabold text-foreground">More ways we get you indexed faster in Google and AI</h3>
          <ul className="mt-3 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
            <li>• Submit every new page in Google Search Console and Bing Webmaster Tools (Bing feeds ChatGPT search)</li>
            <li>• Link new pages from strong, already-indexed pages so crawlers find them quickly</li>
            <li>• Weekly Google Business Profile posts that link to your service pages</li>
            <li>• Fast pages that pass Core Web Vitals on phones</li>
            <li>• Short, direct answers at the top of each page that AI can quote</li>
            <li>• Mentions on Colorado Springs news, chamber and community sites</li>
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/colorado-springs-geo-ai" className="font-bold text-brand-gold-deep underline">Colorado Springs GEO & AI search</Link>{" · "}
            <Link to="/meet-chris" className="font-bold text-brand-gold-deep underline">Meet Chris</Link>{" · "}
            <Link to="/free-audit" className="font-bold text-brand-gold-deep underline">Free audit</Link>
          </p>
        </div>

        {/* Three Springs-specific forces */}
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <article className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <Shield className="h-6 w-6 text-brand-gold-deep" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-extrabold text-foreground">Military churn</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Fort Carson, Peterson SFB, Schriever SFB, and USAFA rotate thousands of families through Colorado Springs every May–August. PCS-season search volume spikes for dentists, mechanics, plumbers, real estate, and family services. We pre-load content and GBP posts to be ranking before rotation hits.
            </p>
          </article>
          <article className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <MapPin className="h-6 w-6 text-brand-gold-deep" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-extrabold text-foreground">Geographic spread</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              The Springs covers 195+ square miles. Briargate, Powers, Old Colorado City, Black Forest, and Monument each have their own Map Pack because Google ranks proximity per query. One generic "Colorado Springs" page can't rank in every neighborhood — you need dedicated pages.
            </p>
          </article>
          <article className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <Mountain className="h-6 w-6 text-brand-gold-deep" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-extrabold text-foreground">Tourism + hail overlay</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Garden of the Gods, Pikes Peak, and Manitou Springs inflate non-buyer search volume year-round. Multiple major hail events every May–September drive massive insurance-restoration demand. Filtering and pre-positioning are SEO levers most agencies miss.
            </p>
          </article>
        </div>

        {/* Neighborhood + Base coverage grid */}
        <div className="mt-12 rounded-2xl border border-border bg-card p-8 shadow-card">
          <h3 className="text-2xl font-extrabold text-foreground">
            Neighborhood-by-neighborhood coverage
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Every Springs sub-market has its own competitive density, demographics, and search intent. Here's how we approach them.
          </p>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {NEIGHBORHOODS.map((n) => (
              <li key={n.name} className="rounded-xl border border-border bg-background p-4">
                <div className="text-sm font-bold text-foreground">{n.name}</div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{n.note}</p>
              </li>
            ))}
          </ul>

          <h4 className="mt-8 text-lg font-extrabold text-foreground">
            Military base coverage
          </h4>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2">
            {BASES.map((b) => (
              <li key={b.name} className="rounded-xl border border-brand-gold/30 bg-brand-gold/5 p-4">
                <div className="text-sm font-bold text-foreground">{b.name}</div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{b.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// vs national agencies (unchanged structure, kept as-is)
// ---------------------------------------------------------------------------

const COMPARISON_ROWS: Array<{ label: string; cwi: string; national: string }> = [
  { label: "Where the team lives & works", cwi: "Colorado Springs HQ — 3210 E Woodmen Rd", national: "Distributed across multiple states" },
  { label: "Who actually does the work", cwi: "The owner + senior in-house team", national: "Junior account managers & offshore subs" },
  { label: "Pricing transparency", cwi: "Public pricing on every service page", national: "Custom quote, contracts behind a sales call" },
  { label: "Contract length", cwi: "Month-to-month, cancel anytime", national: "12–24 month contracts standard" },
  { label: "Local market knowledge", cwi: "Springs neighborhoods, hail cycles, PCS, base communities", national: "Generic 'local SEO' playbook" },
  { label: "GEO / AI search included", cwi: "Built into every package", national: "Upsell or 'coming soon'" },
  { label: "Owner access", cwi: "Direct line to Chris Heidlebaugh", national: "Layers of account management" },
];

function VsNationalBlock() {
  return (
    <section className="bg-background py-20 lg:py-28" aria-labelledby="vs-national-heading">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Colorado Springs SEO vs national SEO firms
          </span>
          <h2 id="vs-national-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            Springs SEO done by <span className="text-gradient-gold">Springs locals</span> — not a national agency
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            A few national SEO firms claim Colorado Springs addresses. That doesn't make
            them local. Here's how an owner-led, Springs-headquartered agency compares to
            the national model on the things that actually move rankings and revenue.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-3">
            <div className="bg-muted/40 p-5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground md:p-6">
              How we compare
            </div>
            <div className="bg-brand-gold/10 p-5 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep md:p-6">
              Colorado Web Impressions
            </div>
            <div className="bg-muted/40 p-5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground md:p-6">
              National SEO firms
            </div>
            {COMPARISON_ROWS.map((row) => (
              <div key={row.label} className="contents">
                <div className="bg-card p-5 text-sm font-semibold text-foreground md:p-6">
                  {row.label}
                </div>
                <div className="flex items-start gap-2 bg-card p-5 text-sm text-foreground md:p-6">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                  <span>{row.cwi}</span>
                </div>
                <div className="flex items-start gap-2 bg-card p-5 text-sm text-muted-foreground md:p-6">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
                  <span>{row.national}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Talk to the owner — not a salesperson
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SpringsLocalProof() {
  return (
    <section className="bg-section-alt py-20 lg:py-28" aria-labelledby="springs-proof-heading">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Real Colorado Springs results
          </span>
          <h2 id="springs-proof-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            What <span className="text-gradient-gold">Springs SEO</span> looks like in practice
          </h2>
        </div>

        {/* Featured Springs case study */}
        <div className="mt-12 overflow-hidden rounded-2xl border border-brand-gold/40 bg-card p-8 shadow-card sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Springs case study
              </div>
              <h3 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                {HVAC_CASE.headline}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {HVAC_CASE.summary}
              </p>
              {HVAC_CASE.quote && (
                <blockquote className="mt-5 border-l-4 border-brand-gold pl-4 text-base italic text-foreground/90">
                  "{HVAC_CASE.quote.text}"
                  <footer className="mt-2 text-sm font-semibold not-italic text-muted-foreground">
                    — {HVAC_CASE.quote.attribution}
                  </footer>
                </blockquote>
              )}
            </div>
            <div className="rounded-xl bg-gradient-gold p-6 text-center text-brand-black">
              <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                Result
              </div>
              <div className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">
                {HVAC_CASE.heroResult}
              </div>
              <Link
                to="/case-studies/$studySlug"
                params={{ studySlug: HVAC_CASE.slug }}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-black px-5 py-2.5 text-sm font-bold text-brand-cream transition-smooth hover:-translate-y-0.5"
              >
                Read the full case study
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Why owners switch to us */}
        <div className="mt-12">
          <div className="text-center">
            <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              Why Colorado Springs owners switch to us
            </h3>
            <p className="mt-3 text-base text-muted-foreground">
              It's not always about being burned. Most owners we talk to are just tired of being pitched, handed off, overcharged, and ignored.
            </p>
          </div>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                pain: "Pitched, then ghosted",
                answer: "You work with the owner — no sales reps, no handoffs after you sign.",
              },
              {
                pain: "Account manager #4 this year",
                answer: "Same Colorado-based person start to finish. One point of contact, period.",
              },
              {
                pain: "$5K/month, zero clarity",
                answer: "Monthly marketing from $950/month. Compare packages on our pricing page. Cancel anytime.",
              },
              {
                pain: "Just another invoice",
                answer: "Capped roster, Colorado Springs based. We pick up the phone — usually same day.",
              },
            ].map((item) => (
              <li
                key={item.pain}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  What you're used to
                </div>
                <div className="mt-2 text-base font-extrabold text-foreground">
                  "{item.pain}"
                </div>
                <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  With us
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                  {item.answer}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Pikes Peak region case studies — 3-up */}
        <div className="mt-12">
          <div className="text-center">
            <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              More Pikes Peak region case studies
            </h3>
            <p className="mt-3 text-base text-muted-foreground">
              Real local clients, anonymized — Colorado Springs, Falcon, and Woodland Park.
            </p>
          </div>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SPRINGS_REGION_CASES.map((cs) => (
              <li
                key={cs.slug}
                className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  {cs.industry} — {cs.location}
                </div>
                <div className="mt-3 text-lg font-extrabold leading-snug text-foreground">
                  {cs.heroResult}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {cs.challenge}
                </p>
                <Link
                  to="/case-studies/$studySlug"
                  params={{ studySlug: cs.slug }}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep transition-smooth hover:gap-3"
                >
                  Read the case study
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Springs reviews grid */}
        {SPRINGS_REVIEWS.length > 0 && (
          <div className="mt-12">
            <div className="text-center">
              <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                What Springs business owners say
              </h3>
            </div>
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {SPRINGS_REVIEWS.map((r) => (
                <li
                  key={`${r.author}-${r.datePublished}`}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <div className="flex items-center gap-1 text-brand-gold">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/85">
                    "{r.body}"
                  </p>
                  <div className="mt-4 text-xs font-semibold text-muted-foreground">
                    {r.author}
                    {r.location && <span> — {r.location}</span>}
                    {r.service && <span> · {r.service}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Springs neighborhood/base related links */}
        <div className="mt-12 rounded-2xl border border-border bg-card p-8 shadow-card">
          <h3 className="text-xl font-extrabold text-foreground">
            More Colorado Springs SEO resources
          </h3>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/contractor-seo-colorado-springs", label: "Contractor SEO — Colorado Springs" },
              { to: "/hvac-seo-colorado-springs", label: "HVAC SEO — Colorado Springs" },
              { to: "/dental-seo-colorado-springs", label: "Dental SEO — Colorado Springs" },
              { to: "/electrical-seo-colorado-springs", label: "Electrical SEO — Colorado Springs" },
              { to: "/colorado-springs-google-maps", label: "Google Maps — Colorado Springs" },
              { to: "/colorado-springs-ppc", label: "PPC — Colorado Springs" },
              { to: "/colorado-springs-web-design", label: "Web Design — Colorado Springs" },
              { to: "/colorado-springs-geo-ai", label: "Generative AI search — Colorado Springs" },
              { to: "/falcon-seo", label: "Falcon SEO" },
              { to: "/woodland-park-seo", label: "Woodland Park SEO" },
              { to: "/monument-seo", label: "Monument SEO" },
              { to: "/pueblo-seo", label: "Pueblo SEO" },
              { to: "/moving-to-colorado-springs", label: "Moving to Colorado Springs — local's guide" },
            ].map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-4 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:text-brand-gold-deep"
                >
                  <span>{link.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Buyer's guides — editorial cluster supporting Colorado Springs SEO */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-8 shadow-card">
          <h3 className="text-xl font-extrabold text-foreground">
            Colorado Springs SEO buyer's guides
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Editorial deep-dives that compare categories (not named competitors) — written for owners weighing how to spend a Colorado Springs SEO budget.
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/learn/local-seo-vs-freelancer", label: "Local SEO agency vs. solo freelancer" },
              { to: "/learn/local-vs-national-seo-agency", label: "Local SEO agency vs. national SEO chain" },
              { to: "/learn/local-seo-vs-lead-marketplaces", label: "Local SEO vs. pay-per-lead marketplaces" },
              { to: "/learn/web-design-vs-freelancer", label: "Local web design agency vs. freelancer" },
              { to: "/learn/custom-web-design-vs-diy-platforms", label: "Custom web design vs. DIY platforms" },
              { to: "/learn/agency-vs-in-house-marketer", label: "Marketing agency vs. in-house marketer" },
            ].map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-4 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:text-brand-gold-deep"
                >
                  <span>{link.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// In-page anchor sections targeted by the SpringsCoverageMap pills
// ---------------------------------------------------------------------------

const ANCHOR_SECTIONS: Array<{
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  places: string[];
}> = [
  {
    id: "in-city",
    eyebrow: "In Colorado Springs",
    title: "Colorado Springs neighborhoods we rank",
    body:
      "From Powers and I-25 to Highway 83 and Highway 24, we serve every Springs sub-market. Each neighborhood has its own Map Pack — Briargate ranks separately from Old Colorado City, and Powers ranks separately from Broadmoor. We build dedicated, neighborhood-aware content so you appear in the 3-pack wherever your buyers actually search.",
    places: [
      "Briargate", "Northgate", "Flying Horse", "Cordera", "Wolf Ranch",
      "Powers Corridor", "Stetson Hills", "Black Forest", "Cimarron Hills",
      "Old Colorado City", "Garden of the Gods", "Broadmoor",
    ],
  },
  {
    id: "eastern-plains",
    eyebrow: "Eastern El Paso County",
    title: "Eastern plains coverage — Falcon to Calhan",
    body:
      "The eastern plains east of Powers are growing faster than any other corner of the metro. Falcon, Peyton, Calhan, and the Ellicott / Mayberry / Percheron communities are full of acreage homes, well systems, propane heat, and trades demand the national agencies completely ignore. We've ranked roofing, HVAC, and well-service businesses out here for years.",
    places: ["Falcon", "Peyton", "Percheron", "Ellicott (Mayberry)", "Calhan"],
  },
  {
    id: "pikes-peak-region",
    eyebrow: "Pikes Peak Region — inner 15 miles",
    title: "Monument, Fountain, Manitou & the inner Pikes Peak Region",
    body:
      "Most Springs service businesses also serve the surrounding Pikes Peak communities. Monument and Tri-Lakes pull affluent north-corridor demand. Fountain and Security-Widefield carry the Fort Carson family economy. Manitou Springs layers tourism demand on top of 8,000 full-time residents. Each gets its own targeted geo footprint in your SEO plan.",
    places: ["Monument", "Tri-Lakes", "Fountain", "Security-Widefield", "Manitou Springs"],
  },
  {
    id: "extended-reach",
    eyebrow: "30-mile extended reach",
    title: "Yes — we still rank you in Woodland Park, Cañon City, Castle Rock, Pueblo & Limon",
    body:
      "Distance from our office doesn't hurt your rankings — your Google Business Profile is anchored at your address, not ours. We actively rank clients in Woodland Park, Cañon City, Castle Rock, Pueblo, and out to Limon on the eastern plains. If you're inside roughly 30 miles of Colorado Springs (and often well beyond), we can build you the same Map Pack + AI-search footprint we build for our in-city clients.",
    places: ["Woodland Park", "Cañon City", "Castle Rock", "Pueblo", "Limon"],
  },
];

function SpringsAnchorSections() {
  return (
    <section
      className="bg-background py-16 lg:py-20"
      aria-labelledby="springs-coverage-anchors-heading"
    >
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="springs-coverage-anchors-heading"
            className="text-3xl font-extrabold text-foreground sm:text-4xl"
          >
            Coverage details by region
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Every city on the map above links here — real coverage, no thin
            doorway pages.
          </p>
        </div>

        <QuotableStat
          stat="We serve a 30-mile radius around our Woodmen Rd office — from Cañon City to Castle Rock, Limon to Woodland Park."
          context="Our Colorado Springs SEO clients span the entire Pikes Peak Region, including Monument, Falcon, Peyton, Calhan, Fountain, Manitou Springs, and out to Pueblo and Cañon City. Distance from our office does not affect your local rankings — your Google Business Profile is anchored at your address."
        />

        <div className="mt-10 space-y-8">
          {ANCHOR_SECTIONS.map((s) => (
            <article
              key={s.id}
              id={s.id}
              className="scroll-mt-24 rounded-2xl border border-border bg-card p-7 shadow-card sm:p-8"
            >
              <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-brand-gold-deep">
                {s.eyebrow}
              </div>
              <h3 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                {s.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-foreground/85">
                {s.body}
              </p>
              <ul className="mt-5 flex flex-wrap gap-2">
                {s.places.map((p) => (
                  <li
                    key={p}
                    className="inline-flex items-center rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3.5 py-1.5 text-xs font-bold text-foreground"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
